/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { useMapContext } from "@evergis/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { insertObjectBefore, removeObject, returnFound } from "find-and";
import { ResourceTypeLink } from "@evergis/api";
import { ConfirmDialogOptions } from "../../../components/Dialogs/Confirmation/Confirmation";

import { goToCatalog, goToLayerConfiguration, goToEqlQuery, goToMainBase } from "../../../ducks/location/location";
import { deleteLayerMeta } from "../../../ducks/project/project";
import { getTempSourceLayers } from "../../../ducks/tempSourceLayers/selectors";
import { deleteTempSourceLayer } from "../../../ducks/tempSourceLayers/tempSourceLayers";
import { isEditable } from "../../../evergis/core/permissions";
import { setLayerTempFilterTool } from "../../../ducks/layerTempFilter/layerTempFilter";
import { openBottomContainer } from "../../../ducks/layout/layout";
import { useLimitsExceededCheck } from "../../../hooks/useLimitsExceededCheck";
import { useResetFeatureCard } from "../../FeatureCard/hooks/useResetFeatureCard";
import { useZoomToLayer } from "./useZoomToLayer";
import { useOpenStyle } from "./useOpenStyle";
import { useProjectInfo, useProjectUpdate } from "../../../ducks/project/hooks";
import { useDefaultScaleRange } from "../../../hooks/style/useDefaultScaleRange";
import { useResetFeatureSelect } from "../../FeatureSelector/hooks/useResetFeatureSelect";
import { layerWithTable } from "../../../resources/layerWithTable";
import { setLayerToShow } from "../../../ducks/showFeature/showFeature";
import { useTableLayerId } from "../../../hooks/table/useTableLayerId";
import { useSelectFeaturesStatisticsActive } from "../../../hooks/selectFeatures/useSelectFeaturesStatisticsActive";
import { useSelectFeaturesLayerName } from "../../../hooks/selectFeatures/useSelectFeaturesLayerName";

import {
  useAddTmsDialog,
  useConfirmDialog,
  useCopyLayerFromProjectDialog,
  useDeleteGroupDialog,
  useDeleteLayerDialog,
  useEditResourceDialog,
  useExportDialog,
  useImportDialog,
  useInfoResourceDialog,
  useLayerAccessDialog,
  useLayerFolderDialog,
  useNewLayerDialog,
  useSaveTempSourceLayer,
  useScaleRestrictionDialog,
  useSelectFilesDialog,
  useUploadDataDialog,
  useWmsLinkDialog,
} from "../../../hooks/useDialogs";
import { useIsShared } from "../../../hooks/api/useIsShared";

import { RoutePanelName } from "../../../appConfig/types";

export const useLayerSelectHandlers = (layerName?: string, noFetch = false) => {
  const defaultScaleRange = useDefaultScaleRange();
  const layerInfos = noFetch ? null : layerWithTable.read({ input: { name: layerName, defaultScaleRange } });
  const [{ name: projectName, content }] = useProjectInfo();
  const { map } = useMapContext();
  const zoomToLayer = useZoomToLayer(layerName, map.crs.wkid);
  const mutation = useProjectUpdate();
  const resetSelected = useResetFeatureSelect();
  const dispatch = useDispatch();
  const [, setTableLayerId] = useTableLayerId();
  const openCopyResourceDialog = useLimitsExceededCheck(useCopyLayerFromProjectDialog());
  const isShared = useIsShared();
  const openExportResourceDialog = useExportDialog();
  const processedImport = useLimitsExceededCheck(useImportDialog(), true);
  const processedCreateLayer = useLimitsExceededCheck(useNewLayerDialog(), true);
  const processedUploadData = useLimitsExceededCheck(useUploadDataDialog(), true);
  const processedCreateTms = useLimitsExceededCheck(useAddTmsDialog(), true);
  const openEditResourceDialog = useEditResourceDialog();
  const openInfoResourceDialog = useInfoResourceDialog();
  const openScaleRestrictionDialog = useScaleRestrictionDialog();
  const openStyle = useOpenStyle(layerName);
  const openLayerAccessDialog = useLayerAccessDialog();
  const openWmsLinkDialog = useWmsLinkDialog();
  const openDeleteLayerDialog = useDeleteLayerDialog();
  const openDeleteGroupDialog = useDeleteGroupDialog();
  const openLayerFolderDialog = useLayerFolderDialog();
  const [, setLayerStatisticsActive] = useSelectFeaturesStatisticsActive();
  const [, setSelectFeaturesLayerName] = useSelectFeaturesLayerName();
  const uploadData = useResetFeatureCard(() => processedUploadData([layerName]), [dispatch]);
  const fromCatalog = useResetFeatureCard(
    () => dispatch(goToCatalog({ tab: ResourceTypeLink.Layer }, { addFromCatalog: true })),
    [dispatch],
  );
  const downloadData = useResetFeatureCard(() => processedImport([null, ResourceTypeLink.Layer]), [dispatch]);
  const createNew = useResetFeatureCard(() => processedCreateLayer([null, ResourceTypeLink.Layer]), [dispatch]);
  const addTmsService = useResetFeatureCard(() => processedCreateTms([null, ResourceTypeLink.Layer]), [dispatch]);
  const layerFilter = useResetFeatureCard(() => {
    dispatch(
      setLayerTempFilterTool({
        active: true,
        layerName,
      }),
    );
  }, [dispatch, layerName]);

  const deleteLayer = useResetFeatureCard(() => {
    openDeleteLayerDialog([layerName]);
  }, [layerName]);

  const layerProperties = useResetFeatureCard(
    () => openEditResourceDialog([layerName, ResourceTypeLink.Layer, layerInfos?.layer?.type]),
    [dispatch, layerName, layerInfos],
  );

  const sourceProperties = useResetFeatureCard(() => {
    if (layerInfos && layerInfos.table) {
      openEditResourceDialog([layerInfos.table.name, ResourceTypeLink.Table, layerInfos?.layer?.type]);
    }
  }, [layerName, layerInfos]);

  const scaleRestriction = useResetFeatureCard(() => openScaleRestrictionDialog([layerName]), [layerName]);

  const confirmDialog = useConfirmDialog();
  const { t } = useTranslation("dialogs");

  const removeLayer = useCallback(() => {
    const canDeleteFromCatalog = layerInfos?.layer ? isEditable(layerInfos?.layer.permissions) : false;
    const canDeleteDataSource = layerInfos?.table ? isEditable(layerInfos?.table.permissions) : false;

    const isTemp = layerName?.startsWith("temp");

    if (isTemp) {
      confirmDialog([""], {
        title: t("deleteLayer.deleteLayer"),
        content: t("deleteLayer.deleteLayerConfirm"),
        onConfirm: () => {
          dispatch(deleteTempSourceLayer(layerName));
        },
        options: {
          maxWidth: "40rem",
        },
      } as ConfirmDialogOptions);

      return;
    }

    if (layerInfos?.layer && (canDeleteFromCatalog || canDeleteDataSource)) {
      deleteLayer();
    } else {
      mutation({
        name: projectName,
        content: {
          ...content,
          items: removeObject(content.items, { name: layerName }),
        },
      });

      dispatch(deleteLayerMeta(layerName));
    }
  }, [deleteLayer, mutation, content, layerInfos, layerName, projectName, dispatch, confirmDialog, t]);

  const removeGroup = useCallback(() => {
    openDeleteGroupDialog([layerName]);
  }, [openDeleteGroupDialog, layerName]);

  const layerInfo = useResetFeatureCard(() => openInfoResourceDialog([layerName, ResourceTypeLink.Layer]), [layerName]);

  const sourceInfo = useResetFeatureCard(() => {
    if (layerInfos && layerInfos.table) {
      openInfoResourceDialog([layerInfos.table.name, ResourceTypeLink.Table]);
    }
  }, [layerName]);

  const layerCopy = useResetFeatureCard(() => {
    dispatch(goToMainBase({ project: projectName, panel: RoutePanelName.Layers }));
    openCopyResourceDialog([layerName, ResourceTypeLink.Layer]);
  }, [layerName, projectName]);

  const layerExport = useResetFeatureCard(
    () =>
      openExportResourceDialog([
        layerName,
        layerInfos && layerInfos.layer && layerInfos.layer.alias,
        ResourceTypeLink.Layer,
      ]),
    [layerName, layerInfos?.layer?.alias],
  );

  const openTable = useCallback(() => {
    if (layerName === undefined) {
      return;
    }

    dispatch(
      goToMainBase({
        project: projectName,
        sharedMap: isShared ? projectName : undefined,
        panel: RoutePanelName.Table,
        layer: layerName,
        query: { layerName },
      }),
    );

    setTableLayerId(layerName);
    dispatch(openBottomContainer());
  }, [dispatch, projectName, layerName, isShared]); // eslint-disable-line

  const openStats = useCallback(() => {
    setLayerStatisticsActive(true);
    setSelectFeaturesLayerName(layerName);
  }, [layerName, setLayerStatisticsActive, setSelectFeaturesLayerName]);

  const layerAccess = useResetFeatureCard(() => openLayerAccessDialog([layerName]), [dispatch, layerName]);

  const copyWms = useResetFeatureCard(
    () =>
      openWmsLinkDialog([layerName, ResourceTypeLink.Layer, layerInfos && layerInfos.layer && layerInfos.layer.alias]),
    [dispatch, layerName, layerInfos],
  );

  const addGroup = useCallback(() => {
    if (layerName) {
      const parent = returnFound(content.items, { name: layerName });

      openLayerFolderDialog([], { parentName: parent.name });
    } else {
      openLayerFolderDialog();
    }
  }, [content?.items, layerName, openLayerFolderDialog]);

  const unGroup = useCallback(() => {
    const parent = returnFound(content.items, { name: layerName });
    let newItems = content.items;

    parent.children.forEach(child => {
      newItems = insertObjectBefore(newItems, { name: layerName }, child);
    });

    newItems = removeObject(newItems, { name: layerName });

    mutation({
      name: projectName,
      content: {
        ...content,
        items: newItems,
      },
    });
  }, [content, layerName, mutation, projectName]);

  const editGroup = useCallback(() => {
    const layer = returnFound(content.items, { name: layerName });

    openLayerFolderDialog([layer.name, layer.alias], { color: layer.clientData?.color });
  }, [content?.items, layerName, openLayerFolderDialog]);

  const addObject = useCallback(() => {
    resetSelected();
    dispatch(setLayerToShow(layerName));
  }, [dispatch, layerName, resetSelected]);

  const configurations = useCallback(() => {
    dispatch(goToLayerConfiguration({ layer: layerName }));
  }, [dispatch, layerName]);

  const openSelectFilesDialog = useSelectFilesDialog();

  const openFileCatalog = useResetFeatureCard(() => openSelectFilesDialog(), []);

  const createEqlQuery = useCallback(() => {
    dispatch(goToEqlQuery({ layer: layerName }));
  }, [dispatch, layerName]);

  const openSaveTempLayerDialog = useSaveTempSourceLayer();
  const tempSourceLayers = useSelector(getTempSourceLayers);

  const saveTempLayer = useResetFeatureCard(
    () => openSaveTempLayerDialog([""], { layer: tempSourceLayers.find(({ name }) => name === layerName) }),
    [layerName, tempSourceLayers, openSaveTempLayerDialog],
  );

  return {
    uploadData,
    fromCatalog,
    downloadData,
    createNew,
    addTmsService,
    addObject,
    addGroup,
    unGroup,
    editGroup,
    style: openStyle,
    layerFilter,
    layerProperties,
    sourceProperties,
    scaleRestriction,
    zoomTo: zoomToLayer,
    removeLayer,
    removeGroup,
    layerInfo,
    sourceInfo,
    layerCopy,
    layerExport,
    openTable,
    openStats,
    layerAccess,
    copyWms,
    configurations,
    openFileCatalog,
    createEqlQuery,
    saveTempLayer,
  };
};
